/**
 * StepRoute — the per-step guard + shell wrapper (the "resumable step router" enforcement point).
 *
 * For a given step it: shows the calm loading state while the token resolves; maps a failed
 * resolution to the right terminal page; redirects if the referee tried to open a step ahead of
 * where the server says they are (step guard); and otherwise renders the step inside the AppShell
 * (flow steps) or bare (terminal steps, which are full-page calm layouts).
 */
import type { ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { AppShell } from '@/components/AppShell';
import { SessionError, SessionLoading } from '@/components/SessionStates';
import { TerminalPage } from '@/components/TerminalPage';
import { RefereeApiError } from '@/lib/api-types';
import { isFlowStep, type RefereeStep } from './steps';
import { useSession } from './useSession';

/** Map a context-load failure to a page. `not_found`/`gone` are calm terminals; the rest retry. */
function ErrorFallback({ error, onRetry }: { error: unknown; onRetry: () => void }) {
  if (error instanceof RefereeApiError) {
    if (error.kind === 'not_found') return <TerminalPage kind="not-found" />;
    if (error.kind === 'gone') return <TerminalPage kind="expired" />;
  }
  return <SessionError onRetry={onRetry} />;
}

export function StepRoute({ step, children }: { step: RefereeStep; children: ReactNode }) {
  const session = useSession();

  if (session.isLoading) return <SessionLoading />;
  if (session.isError) return <ErrorFallback error={session.error} onRetry={session.refetch} />;
  if (!session.context || !session.currentStep) return <SessionLoading />;

  // Step guard: never let the referee sit on a step ahead of the server's truth; send them to
  // the step they actually belong on (also handles a terminal status locking to its page).
  if (!session.canAccess(step)) {
    return <Navigate to={`/r/${session.token}/${session.currentStep}`} replace />;
  }

  if (isFlowStep(step)) {
    return (
      <AppShell step={step} context={session.context}>
        {children}
      </AppShell>
    );
  }
  return <>{children}</>;
}
